/**
 * /auth/sso-callback — the redirect half of Jubit unified auth.
 *
 * jubit.ai's cross-product registry advertises this exact path for `jubuddy` and
 * `gghere`, and `auth-jubit-sso`'s `create_token` builds every `redirect_url` from
 * it. Before this route existed the path 404'd here, so a real handoff burned its
 * one-time token on "No buddy lives here" and stranded the user signed out.
 *
 * Distinct from `/auth/callback` (`AuthCallback.tsx`), which handles Supabase's own
 * PKCE `?code=` exchange. This one redeems a Jubit `?token=` and sets the session
 * itself.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { isAuthConfigured, supabase } from '../lib/supabase.ts';
import { redeemJubitSsoToken, safeSsoNext } from '../auth/jubitSsoToken.ts';
import { useUserStore } from '../state/userStore.ts';

export function SsoCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refresh = useUserStore((s) => s.refresh);
  const [error, setError] = useState<string | null>(null);

  // The exchange token is single-use — a second redemption always fails. Without
  // this guard StrictMode's double-invoke (or any re-render handing back a fresh
  // `params` identity) would spend the token and report a good sign-in as expired.
  const redeemed = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const token = params.get('token');

    if (redeemed.current === token) return;
    redeemed.current = token;

    async function run() {
      if (!isAuthConfigured) {
        setError('Sign-in is not configured for this environment.');
        return;
      }

      const result = await redeemJubitSsoToken(token);
      if (cancelled) return;

      if (!result.ok) {
        setError(result.message);
        return;
      }

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: result.session.accessToken,
        refresh_token: result.session.refreshToken,
      });
      if (cancelled) return;

      if (sessionError) {
        setError(sessionError.message);
        return;
      }

      // Pull profile + effective tier so the UI reflects the Jubit entitlement
      // immediately rather than on the next navigation.
      await refresh();
      if (cancelled) return;

      navigate(safeSsoNext(params.get('next')), { replace: true });
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate, params, refresh]);

  return (
    <div className="flex min-h-screen flex-col bg-jtk-surface-1 text-jtk-primary">
      <header className="flex items-center justify-between border-b border-jtk-subtle px-4 py-3">
        <Link to="/" className="text-xs font-bold tracking-tight text-jub-tile hover:brightness-110">
          ← Jubuddy
        </Link>
        <span className="text-xs uppercase tracking-wider text-jtk-whisper">Sign in</span>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        {error ? (
          <section
            role="alert"
            className="w-full max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center"
          >
            <h1 className="text-lg font-bold text-red-200">Sign-in failed</h1>
            <p className="mt-2 text-sm text-red-100">{error}</p>
            <Link to="/" className="mt-4 inline-block text-xs underline">
              Back to Jubuddy
            </Link>
          </section>
        ) : (
          <div
            className="flex items-center gap-3 text-sm text-jtk-secondary"
            role="status"
            aria-live="polite"
          >
            <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-jub-tile" aria-hidden />
            Completing sign-in…
          </div>
        )}
      </main>
    </div>
  );
}
